import { useState } from 'react';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

const DB_FIELDS_CLIENTS = [
  { value: '', label: '-- Skip Column --' },
  { value: 'company_name', label: 'Company Name', required: false },
  { value: 'first_name', label: 'First Name', required: false },
  { value: 'last_name', label: 'Last Name', required: false },
  { value: 'email', label: 'Email', required: false },
  { value: 'phone', label: 'Phone', required: false },
  { value: 'mobile', label: 'Mobile', required: false },
  { value: 'address_line1', label: 'Address Line 1', required: false },
  { value: 'address_line2', label: 'Address Line 2', required: false },
  { value: 'city', label: 'City', required: false },
  { value: 'county', label: 'County', required: false },
  { value: 'postcode', label: 'Postcode', required: false },
  { value: 'country', label: 'Country', required: false },
  { value: 'credit_limit', label: 'Credit Limit', required: false },
  { value: 'notes', label: 'Notes', required: false },
];

const DB_FIELDS_SUPPLIERS = [
  { value: '', label: '-- Skip Column --' },
  { value: 'supplier_name', label: 'Supplier Name', required: true },
  { value: 'supplier_code', label: 'Supplier Code', required: false },
  { value: 'contact_name', label: 'Contact Name', required: false },
  { value: 'email', label: 'Email', required: false },
  { value: 'phone', label: 'Phone', required: false },
  { value: 'mobile', label: 'Mobile', required: false },
  { value: 'address_line1', label: 'Address Line 1', required: false },
  { value: 'address_line2', label: 'Address Line 2', required: false },
  { value: 'city', label: 'City', required: false },
  { value: 'county', label: 'County', required: false },
  { value: 'postcode', label: 'Postcode', required: false },
  { value: 'country', label: 'Country', required: false },
  { value: 'payment_terms', label: 'Payment Terms', required: false },
  { value: 'credit_limit', label: 'Credit Limit', required: false },
  { value: 'account_number', label: 'Account Number', required: false },
  { value: 'notes', label: 'Notes', required: false },
];

const DB_FIELDS_CONTACTS = [
  { value: '', label: '-- Skip Column --' },
  { value: 'client_company_name', label: 'Client Company Name (for matching)', required: false },
  { value: 'client_email', label: 'Client Email (for matching)', required: false },
  { value: 'contact_first_name', label: 'Contact First Name', required: true },
  { value: 'contact_last_name', label: 'Contact Last Name', required: false },
  { value: 'contact_email', label: 'Contact Email', required: false },
  { value: 'contact_phone', label: 'Contact Phone', required: false },
  { value: 'contact_mobile', label: 'Contact Mobile', required: false },
  { value: 'contact_position', label: 'Position/Title', required: false },
  { value: 'contact_notes', label: 'Contact Notes', required: false },
];

export default function ImportColumnMapper({ mappings, onMappingsChange, onAutoDetect, importType = 'clients' }) {
  const [localMappings, setLocalMappings] = useState(mappings || []);

  const handleMappingChange = (csvColumn, dbField) => {
    const updated = localMappings.map(m => 
      m.csvColumn === csvColumn 
        ? { ...m, dbField: dbField || null }
        : m
    );
    setLocalMappings(updated);
    onMappingsChange(updated);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-[var(--ds-status-success)]';
    if (confidence >= 0.5) return 'text-[var(--ds-status-warning)]';
    return 'text-[var(--ds-text-muted)]';
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 0.8) return <CheckCircle className="h-4 w-4 text-[var(--ds-status-success)]" />;
    if (confidence >= 0.5) return <AlertCircle className="h-4 w-4 text-[var(--ds-status-warning)]" />;
    return null;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">
              Map CSV Columns to Database Fields
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={onAutoDetect}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Auto-Detect
            </Button>
          </div>
          <p className="text-body text-[var(--ds-text-secondary)]">
            Match each column from your CSV file to the corresponding database field. 
            Fields marked with high confidence have been auto-detected.
          </p>
        </div>

        <div className="space-y-3">
          {localMappings.map((mapping, index) => (
            <div
              key={index}
              className="flex items-center gap-4 p-3 rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel2)] border border-[var(--ds-border-subtle)]"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-body font-medium text-[var(--ds-text-primary)] truncate">
                    {mapping.csvColumn}
                  </span>
                  {mapping.confidence > 0 && (
                    <div className="flex items-center gap-1">
                      {getConfidenceIcon(mapping.confidence)}
                      <span className={`text-micro ${getConfidenceColor(mapping.confidence)}`}>
                        {Math.round(mapping.confidence * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-1">
                <span className="text-micro text-[var(--ds-text-muted)]">→</span>
                <Select
                  value={mapping.dbField || ''}
                  onChange={(e) => handleMappingChange(mapping.csvColumn, e.target.value)}
                  className="flex-1 h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {(importType === 'client-contacts' 
                    ? DB_FIELDS_CONTACTS 
                    : importType === 'suppliers'
                    ? DB_FIELDS_SUPPLIERS
                    : DB_FIELDS_CLIENTS).map(field => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ))}
        </div>

        {localMappings.length === 0 && (
          <div className="text-center py-8 text-body text-[var(--ds-text-muted)]">
            No columns detected. Please upload a file first.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

